import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, Loader2, X } from 'lucide-react';
import { apiClient } from '../utils/api';

export function DocumentViewerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preferredFormat = (searchParams.get('format') || 'pdf').toLowerCase() === 'docx' ? 'docx' : 'pdf';

  const initialVariant = (searchParams.get('variant') || '').toLowerCase() === 'original' ? 'original' : 'default';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('document');
  const [activeFormat, setActiveFormat] = useState<'pdf' | 'docx'>(preferredFormat);
  const [variant, setVariant] = useState<'default' | 'original'>(initialVariant);

  const canEmbed = useMemo(() => activeFormat === 'pdf' && !!blobUrl, [activeFormat, blobUrl]);

  useEffect(() => {
    let revokedUrl: string | null = null;

    const loadDocument = async () => {
      if (!id) {
        setError('Document introuvable');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const attemptFormats: Array<'pdf' | 'docx'> = preferredFormat === 'pdf' ? ['pdf', 'docx'] : ['docx', 'pdf'];
      for (const format of attemptFormats) {
        try {
          // The variant only applies to the PDF (original vs signed); DOCX has no signed variant.
          const query = format === 'pdf' && variant === 'original' ? '?variant=original' : '';
          const response = await apiClient(`/api/documents/${id}/${format}${query}`, { method: 'GET' });
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          revokedUrl = url;
          setBlobUrl(url);
          setActiveFormat(format);
          setFileName(`document-${id}.${format}`);
          setLoading(false);
          return;
        } catch {
          // Try the fallback format
        }
      }

      setError('Impossible de charger ce document');
      setLoading(false);
    };

    void loadDocument();

    return () => {
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [id, preferredFormat, variant]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCloseWindow = () => {
    window.close();
    window.setTimeout(() => navigate(-1), 150);
  };

  const handleOpenNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="h-[calc(100vh-4rem)] bg-gray-100 flex flex-col">
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={handleBack}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <span className="text-sm text-gray-500">Format: {activeFormat.toUpperCase()}</span>
          {activeFormat === 'pdf' && (
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden ml-2">
              <button
                onClick={() => setVariant('default')}
                className={`px-3 py-1.5 text-xs ${variant === 'default' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Version signée
              </button>
              <button
                onClick={() => setVariant('original')}
                className={`px-3 py-1.5 text-xs border-l border-gray-300 ${variant === 'original' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Version originale
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenNewTab}
            disabled={!blobUrl}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Ouvrir dans un onglet
          </button>
          <button
            onClick={handleDownload}
            disabled={!blobUrl}
            className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Télécharger
          </button>
          <button
            onClick={handleCloseWindow}
            className="px-3 py-2 text-sm rounded-md bg-gray-800 text-white hover:bg-gray-900 inline-flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Fermer
          </button>
        </div>
      </div>

      <div className="flex-1 p-4">
        {loading && (
          <div className="h-full flex items-center justify-center text-gray-600 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Chargement du document...
          </div>
        )}

        {!loading && error && (
          <div className="h-full flex items-center justify-center text-red-600">
            {error}
          </div>
        )}

        {!loading && !error && blobUrl && canEmbed && (
          <iframe
            src={blobUrl}
            title="Document Viewer"
            className="w-full h-full rounded-lg border border-gray-300 bg-white"
          />
        )}

        {!loading && !error && blobUrl && !canEmbed && (
          <div className="h-full flex items-center justify-center">
            <div className="bg-white border border-gray-200 rounded-lg p-6 text-center max-w-md">
              <p className="text-sm text-gray-700 mb-4">
                L'aperçu direct n'est pas disponible pour ce format. Vous pouvez l'ouvrir dans un onglet ou le télécharger.
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={handleOpenNewTab}
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ouvrir
                </button>
                <button
                  onClick={handleDownload}
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Télécharger
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
